import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Activity, BookOpen, Boxes, CornerDownLeft, CreditCard, FileText, Globe2, History, Images, KeyRound, LayoutDashboard, Languages, LogOut, Monitor, Moon, ScrollText, Search, Sun, Users } from "lucide-react";
import { ROLE_INFO, can } from "@jange/shared";
import { api } from "../lib/api";
import { useStudio } from "../lib/context";

type Item = { id: string; group: string; label: string; hint?: string; icon: ReactNode; run: () => void; keywords?: string };
const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Palette de commandes (⌘K) : aller à une page, ouvrir une notion par son titre, changer de thème. */
export function CommandPalette() {
  const { paletteOpen, setPaletteOpen } = useStudio();
  if (!paletteOpen) return null;
  return <Palette onClose={() => setPaletteOpen(false)} />;
}

function Palette({ onClose }: { onClose: () => void }) {
  const { teacher, countryId, recent, setTheme, signOut } = useStudio();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const roles = teacher?.roles ?? [];
  const content = roles.some((r) => (ROLE_INFO as Record<string, { family: string }>)[r]?.family === "content");
  const system = can(roles, "system.view");
  const tree = useQuery({ queryKey: ["curricula", countryId], queryFn: () => api.curricula(countryId), enabled: content, staleTime: 60_000 });

  const items = useMemo<Item[]>(() => {
    const go = (to: string) => () => { nav(to); onClose(); };
    const pages: Item[] = [
      ...(content ? [
        { id: "p-dash", group: "Pages", label: "Tableau de bord", icon: <LayoutDashboard size={16} />, run: go("/") },
        { id: "p-prog", group: "Pages", label: "Programme", icon: <BookOpen size={16} />, run: go("/programme"), keywords: "notions chapitres" },
        { id: "p-bank", group: "Pages", label: "Banque 3D", icon: <Boxes size={16} />, run: go("/banque"), keywords: "modeles 3d coeur adn organes molecules svt" },
        { id: "p-med", group: "Pages", label: "Médiathèque", icon: <Images size={16} />, run: go("/medias"), keywords: "video image audio 3d envoyer" },
        { id: "p-glo", group: "Pages", label: "Glossaire", icon: <Languages size={16} />, run: go("/glossaire"), keywords: "traduction wolof termes" },
      ] : []),
      ...(system ? [
        { id: "s-etat", group: "Administration", label: "État du service", icon: <Activity size={16} />, run: go("/systeme"), keywords: "api redis base" },
        { id: "s-pays", group: "Administration", label: "Pays", icon: <Globe2 size={16} />, run: go("/systeme/pays"), keywords: "prix paiement ouverture" },
        { id: "s-form", group: "Administration", label: "Formules", icon: <CreditCard size={16} />, run: go("/systeme/formules"), keywords: "abonnement essai acces" },
        { id: "s-four", group: "Administration", label: "Fournisseurs", icon: <KeyRound size={16} />, run: go("/systeme/fournisseurs"), keywords: "cle api sms whatsapp voix tuteur ia" },
        { id: "s-team", group: "Administration", label: "Équipe", icon: <Users size={16} />, run: go("/systeme/equipe"), keywords: "membres roles" },
        { id: "s-jour", group: "Administration", label: "Journal", icon: <ScrollText size={16} />, run: go("/systeme/journal"), keywords: "audit historique" },
      ] : []),
    ];
    const recents: Item[] = recent.map((r) => ({ id: `r-${r.id}`, group: "Récemment ouvertes", label: r.title, icon: <History size={16} />, run: go(`/notions/${r.id}`) }));
    const notions: Item[] = (tree.data ?? []).flatMap((cv) => cv.chapters.flatMap((ch) => ch.notions.map((n) => ({
      id: `n-${n.id}`, group: "Notions", label: n.title, hint: `Ch. ${ch.ordinal} · ${ch.title}`, icon: <FileText size={16} />, run: go(`/notions/${n.id}`), keywords: `${n.slug} ${ch.title}`,
    }))));
    const actions: Item[] = [
      { id: "a-light", group: "Actions", label: "Thème clair", icon: <Sun size={16} />, run: () => { setTheme("light"); onClose(); }, keywords: "apparence" },
      { id: "a-dark", group: "Actions", label: "Thème sombre", icon: <Moon size={16} />, run: () => { setTheme("dark"); onClose(); }, keywords: "apparence nuit" },
      { id: "a-sys", group: "Actions", label: "Thème du système", icon: <Monitor size={16} />, run: () => { setTheme("system"); onClose(); }, keywords: "apparence auto" },
      { id: "a-out", group: "Actions", label: "Se déconnecter", icon: <LogOut size={16} />, run: () => { onClose(); signOut(); } },
    ];
    const f = norm(q.trim());
    if (!f) return [...recents, ...pages, ...actions];
    const words = f.split(/\s+/);
    const match = (x: Item) => { const hay = norm(`${x.label} ${x.keywords ?? ""} ${x.hint ?? ""}`); return words.every((w) => hay.includes(w)); };
    return [...pages.filter(match), ...notions.filter(match).slice(0, 30), ...actions.filter(match)];
  }, [q, content, system, recent, tree.data, nav, onClose, setTheme, signOut]);

  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => setActive(0), [q]);
  useEffect(() => { list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" }); }, [active]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); items[active]?.run(); }
  };

  let lastGroup = "";
  return (
    <>
      <div className="backdrop cmdk-bg" onClick={onClose} />
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Recherche et commandes" onKeyDown={onKey}>
        <div className="cmdk-input">
          <Search size={18} color="var(--text-3)" />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une notion, une page, une action…"
            role="combobox" aria-expanded="true" aria-controls="cmdk-list" aria-activedescendant={items[active] ? `cmdk-${items[active]!.id}` : undefined} aria-autocomplete="list" />
          <span className="kbd">Échap</span>
        </div>
        <div className="cmdk-list" id="cmdk-list" role="listbox" ref={list} aria-label="Résultats">
          {items.length === 0 && <div className="empty" style={{ padding: 28 }}><b>Aucun résultat</b><span className="small">Essayez un autre mot du titre de la notion.</span></div>}
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? <div className="cmdk-group" role="presentation">{it.group}</div> : null;
            lastGroup = it.group;
            return (
              <div key={it.id}>
                {head}
                <div id={`cmdk-${it.id}`} data-i={i} role="option" aria-selected={i === active} className="cmdk-item" onMouseMove={() => setActive(i)} onClick={it.run}>
                  {it.icon}<span>{it.label}</span>{it.hint && <span className="hint">{it.hint}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="cmdk-foot"><span><span className="kbd">↑</span> <span className="kbd">↓</span> naviguer</span><span><span className="kbd"><CornerDownLeft size={11} /></span> ouvrir</span><span><span className="kbd">/</span> ouvre la recherche</span></div>
      </div>
    </>
  );
}
